import { exhaustiveFail } from '@cbridge-utils/error';
import {
  IApiLpConnectionBase, IApiLpConnectionState, ApiLpConnectionStatus, IApiLpAdapterSetting, IApiLpConnection, IApiLpAdapterSchema,
} from '@cbridge-packages/api';
import {
  IAdminApiLp, IAdminApiLpCreate, IApiGatewayLp, ApiGatewayLpStatus, IAdminApiLpDetails, ApiLpErrorResolutionPolicy,
  ApiLpIntegrationDirection,
} from '@cbridge-packages/api-admin';
import {
  DEMO_CBRIDGE_ADAPTER, ILpAdapter, LpIntegrationDirection, getReadOnlyParameterKeys, isInboundAdapter, isTradingDisabled,
} from '@cbridge-packages/lp-adapters';
import {
  ILp, ILpForm, IGatewayLp, LpGatewayStatus, ILpConnectionState, LpConnectionStatus, ILpConnectionForm, ILpAdapterSetting,
} from './lp.interface';

interface IToApiConnectionOptions {
  // Inbound sessions are opened by the LP: CP owns host/port/login/password.
  omitEndpoint?: boolean;
  // Parameters CP marks `readOnly` are never sent back.
  schema?: IApiLpAdapterSchema | null;
}

export namespace LpTranslator {
  export function fromApi(lp: IAdminApiLp): ILp {
    return {
      lpId: lp.id,
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      throttlingInterval: lp.throttlingInterval,
      errorResolutionPolicy: lp.errorResolution?.policy ?? ApiLpErrorResolutionPolicy.Manual,
      resetSeqNumFlagSupported: lp.errorResolution?.resetSeqNumFlagSupported ?? false,
      pricing: LpTranslator.fromApiLpConnectionState(lp.pricing),
      trading: lp.trading ? LpTranslator.fromApiLpConnectionState(lp.trading) : undefined,
      gateways: lp.gateways?.map(LpTranslator.fromApiGatewayLp),
    };
  }

  export function fromApiLpConnectionState(state: IApiLpConnectionState): ILpConnectionState {
    switch (state.status) {
      case ApiLpConnectionStatus.Error:
        return { ...state, status: LpConnectionStatus.Error };
      case ApiLpConnectionStatus.Offline:
        return { ...state, status: LpConnectionStatus.Offline };
      case ApiLpConnectionStatus.Online:
        return { ...state, status: LpConnectionStatus.Online };
      case ApiLpConnectionStatus.Backup:
        return { ...state, status: LpConnectionStatus.Backup };
      default:
        exhaustiveFail(state.status);
    }
  }

  export function fromApiGatewayLp(gwLp: IApiGatewayLp): IGatewayLp {
    return {
      id: gwLp.id,
      name: gwLp.name,
      available: gwLp.available,
      status: gwLp.available
        ? LpTranslator.fromApiGatewayStatus(gwLp.status)
        : LpGatewayStatus.Error,
    };
  }

  export function fromApiGatewayStatus(status: ApiGatewayLpStatus): LpGatewayStatus {
    if (status === ApiGatewayLpStatus.Active) {
      return LpGatewayStatus.Active;
    } else {
      return LpGatewayStatus.Backup;
    }
  }

  // CP omits the connections (or their fields) for the DEMO_CBRIDGE adapter
  export function fromApiConnection(lpConnection?: IApiLpConnection): ILpConnectionForm {
    const adapterSettings: ILpAdapterSetting[] = (lpConnection?.adapter?.settings ?? [])
      .map((item, index) => ({ ...item, id: index }));

    return {
      host: lpConnection?.host,
      port: lpConnection?.port,
      login: lpConnection?.login,
      password: lpConnection?.password,
      adapterSettings,
    };
  }

  export function fromApiDetails(lp: IAdminApiLpDetails): ILpForm {
    return {
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      throttlingInterval: lp.throttlingInterval ?? null,
      errorResolutionPolicy: lp.errorResolution?.policy ?? ApiLpErrorResolutionPolicy.Manual,
      resetSeqNumFlagSupported: lp.errorResolution?.resetSeqNumFlagSupported ?? false,
      pricing: LpTranslator.fromApiConnection(lp.pricing),
      // The demo adapter comes without connections, a trading-less adapter without `trading`
      trading: lp.trading || lp.adapter === DEMO_CBRIDGE_ADAPTER ? LpTranslator.fromApiConnection(lp.trading) : undefined,
      gateway1: lp.gateways?.[0]?.id,
      gateway2: lp.gateways?.[1]?.id,
      gateway3: lp.gateways?.[2]?.id,
    };
  }

  export function toApiConnection(
    lpConnectionFormData: ILpConnectionForm = {},
    options: IToApiConnectionOptions = {},
  ): IApiLpConnectionBase {
    const readOnlyKeys = getReadOnlyParameterKeys(options.schema);
    const adapterSettings: IApiLpAdapterSetting[] = (lpConnectionFormData.adapterSettings ?? [])
      .filter(({ key, value }) => key?.trim() && value?.trim() && !readOnlyKeys.has(key))
      .map(({ key, value }) => ({ key, value }));

    if (options.omitEndpoint) {
      return { adapterSettings };
    }

    return {
      host: lpConnectionFormData.host,
      port: lpConnectionFormData.port,
      login: lpConnectionFormData.login,
      password: lpConnectionFormData.password,
      adapterSettings,
    };
  }

  export function toApiIntegrationDirection(direction: LpIntegrationDirection): ApiLpIntegrationDirection {
    return direction === LpIntegrationDirection.Inbound ? ApiLpIntegrationDirection.Inbound : ApiLpIntegrationDirection.Outbound;
  }

  // `adapter` — the LP's adapter from the dictionary (undefined for a legacy adapter or an
  // unloaded list): its direction and schemas decide what the connections carry.
  export function toApi(lp: ILpForm, adapter?: ILpAdapter | null): IAdminApiLpCreate {
    const { gateway1, gateway2, gateway3 } = lp;

    const gateways: IAdminApiLpCreate['gateways'] = [gateway1];
    if (gateway2) {
      gateways.push(gateway2);
    }
    if (gateway3) {
      gateways.push(gateway3);
    }

    // The demo adapter connections are preconfigured on the CP side
    const isDemoAdapter = lp.adapter === DEMO_CBRIDGE_ADAPTER;
    const schemas = adapter?.schemas;

    return {
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      throttlingInterval: lp.throttlingInterval ?? undefined,
      errorResolution: {
        policy: lp.errorResolutionPolicy,
        resetSeqNumFlagSupported: lp.resetSeqNumFlagSupported,
      },
      pricing: isDemoAdapter
        ? undefined
        : LpTranslator.toApiConnection(lp.pricing, { omitEndpoint: isInboundAdapter(adapter), schema: schemas?.pricing }),
      trading: isDemoAdapter || isTradingDisabled(schemas)
        ? undefined
        : LpTranslator.toApiConnection(lp.trading, { schema: schemas?.trading }),
      gateways,
    };
  }

  export function toEditableAdapterSettings(lp: ILpForm = {}) {
    const { pricing, trading } = lp;
    const pricingSettings = pricing?.adapterSettings ?? [];
    const tradingSettings = trading?.adapterSettings ?? [];

    return {
      ...lp,
      pricing: {
        ...pricing,
        adapterSettings: [...pricingSettings, { id: pricingSettings.length }],
      },
      trading: {
        ...trading,
        adapterSettings: [...tradingSettings, { id: tradingSettings.length }],
      },
    };
  }
}
